import { hideDialog } from '../../../../../react/features/base/dialog/actions';
import Dialog from '../../../../../react/features/base/ui/components/web/Dialog';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    hideRequestControlDialog,
    openDialogWithSharing,
    userApproved, userRejected
} from '../../../../components/wildix-remote-control/actions';
import { STATUSES } from '../../../../components/wildix-remote-control/constants';
import { isScreenSharingPlayingByUserID } from '../../../../components/wildix-remote-control/functions';

type Props = {
    launchedControlID?: string,
    controlledUserId?: string,
    isUserGivesSelfControl?: boolean,
    launchedName?: string
}

function usePrevious(value) {
    const ref = useRef();

    useEffect(() => {
        ref.current = value;
    });

    return ref.current;
}


const RequestControlDialog = ({
    launchedControlID, controlledUserId, isUserGivesSelfControl, launchedName
}: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isScreenSharingPlaying = useSelector(isScreenSharingPlayingByUserID);
    const refScreenSharingPlaying = useRef(isScreenSharingPlaying);
    const { statusControl } = useSelector(state => state['wildix/features/control-sharing']);
    const prevStatus = usePrevious(statusControl);

    const closeDialog = useCallback(() => {
        dispatch(hideDialog());
    }, [ dispatch ]);

    useEffect(() => {
        refScreenSharingPlaying.current = isScreenSharingPlaying;
    }, [ isScreenSharingPlaying ]);

    useEffect(() => {
        if (isUserGivesSelfControl && prevStatus === STATUSES.REQUESTING && statusControl === STATUSES.CLOSED) {
            dispatch(hideRequestControlDialog());
        }
    }, [ statusControl ]);

    const getDialogTitle = useMemo(() => {
        const nameParams = { name: launchedName };

        if (isUserGivesSelfControl) {
            return t('controlSharing.userGivesControl', nameParams);
        }

        return t('controlSharing.userWantsControl', nameParams);
    }, [ isUserGivesSelfControl, launchedName ]);

    const onCancel = useCallback(() => {
        dispatch(userRejected(launchedControlID));
        closeDialog();
    }, [ launchedControlID ]);

    const onSubmit = useCallback(() => {
        if (isUserGivesSelfControl) {
            dispatch(userApproved(controlledUserId, launchedControlID));
        } else if (refScreenSharingPlaying.current) {
            dispatch(userApproved(launchedControlID, controlledUserId));
        } else {
            dispatch(openDialogWithSharing(launchedControlID, controlledUserId));
        }

        closeDialog();
    }, [ launchedControlID, controlledUserId, isUserGivesSelfControl ]);

    return (
        <>
            <Dialog
                onCancel={ onCancel }
                onSubmit={ onSubmit }
                titleKey = 'controlSharing.titleDialogControl'
                size = 'medium'>
                { getDialogTitle }
            </Dialog>
        </>
    );
};

export default RequestControlDialog;
